import { Suspense, lazy, Component, type ReactNode, type ErrorInfo } from "react";
import { StaleBuildReload } from "./lib/stale-build";
import WhoPage from "./pages/WhoPage";
import MePage from "./pages/MePage";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

class ChunkErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(err: unknown): { failed: boolean } {
    const name = (err as { name?: string })?.name;
    const message = (err as { message?: string })?.message ?? "";
    const isChunkError =
      name === "ChunkLoadError" ||
      /Loading chunk \d+ failed/i.test(message) ||
      /Loading CSS chunk/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message);
    if (isChunkError) {
      window.location.reload();
      return { failed: true };
    }
    throw err;
  }

  componentDidCatch(_err: unknown, _info: ErrorInfo): void {}

  render(): ReactNode {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

import { RouteTitle } from "./lib/title";
/* § F158 → § F201 — the wall in front of every route is a SIGNED-IN ACCOUNT
   now (it was the family password). See `components/AppGate.tsx`: a gated
   deep link goes to `/login?next=…` and comes back. */
import { AppGate } from "./components/AppGate";
import { RotateGuard } from "./components/RotateGuard";

const HomePage = lazy(() => import("./pages/HomePage"));
const TrivianightPage = lazy(() => import("./pages/TrivianightPage"));
const PartyPage = lazy(() => import("./pages/PartyPage"));
const DailyChallengesPage = lazy(() => import("./pages/DailyChallengesPage"));
/* § F170 (2026-09-17) — the two new hub categories. Lazy like the rest: the
   group menu is two links, but the turn-based surface carries a Wordle board
   and the duel client, which nothing else on the hub needs. */
const GroupGamesPage = lazy(() => import("./pages/GroupGamesPage"));
const TurnBasedPage = lazy(() => import("./pages/TurnBasedPage"));
/* F45 (2026-08-06) — Game Table came in off the `gametable.` subdomain and is a
   route like the other three categories. Lazy like the rest: it carries a Web
   Bluetooth client and a stylesheet that nothing else on the hub needs. */
const GameTablePage = lazy(() => import("./pages/GameTablePage"));
/* § F220 (2026-10-26) — Puzzles: shared, live crosswords from Jake's scans. */
const PuzzlesPage = lazy(() => import("./pages/PuzzlesPage"));
/* § F201 — accounts and groups. */
const AuthPage = lazy(() => import("./pages/AuthPage"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const ForgotPasswordPage = lazy(() => import("./pages/PasswordPages").then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/PasswordPages").then((m) => ({ default: m.ResetPasswordPage })));
const GroupsPage = lazy(() => import("./pages/GroupsPage"));
const TermsPage = lazy(() => import("./pages/LegalPage").then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("./pages/LegalPage").then((m) => ({ default: m.PrivacyPage })));

function PageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/** Anything unrecognised lands on the hub, query and hash intact. */
function LegacyRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/", search: location.search, hash: location.hash }}
      replace
    />
  );
}

/**
 * `/trivia-night` was the pre-migration path and is still in bookmarks +
 * the R20 printpartyhub 301s; it now points at the trivia app's own route
 * rather than at the hub.
 */
function TriviaAlias() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/trivia", search: location.search, hash: location.hash }}
      replace
    />
  );
}

/**
 * F26 (2026-07-27) — the party surface is `/doodleduel`; it was `/party` until
 * Jake renamed the user-facing page after the game. Every QR ever shown in a
 * lobby, every link ever texted and every bookmark points at the old path with
 * a `?code=` on it, so `/party` and `/party?code=XXXX` land here and are
 * replaced with the new path, query AND hash intact. `replace` rather than a
 * push, so Back from /doodleduel does not bounce through the alias.
 *
 * Only the PAGE moved. `/api/party/*` and the socket at `/party/ws` are
 * internal wire names that no human types; renaming them would be churn with
 * deploy risk and buys nothing.
 */
function PartyAlias() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/doodleduel", search: location.search, hash: location.hash }}
      replace
    />
  );
}

/**
 * § F169 (2026-09-17) — Mini-Games became DAILY CHALLENGES, and every path it
 * ever had is still in somebody's history, on somebody's home screen and in
 * the emails the fill batch has sent. `/minigames`, `/minigames/queens`,
 * `/minigames/day/2026-09-01` and the rest land here and are replaced by the
 * same path under `/daily-challenges`, query and hash intact.
 *
 * `replace`, like `PartyAlias` above and for the same reason: Back from the
 * new path must not bounce through the old one.
 */
function DailyChallengesAlias() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/minigames/, "");
  return (
    <Navigate
      to={{ pathname: `/daily-challenges${rest}`, search: location.search, hash: location.hash }}
      replace
    />
  );
}

/** § F179 — `/turn-based…` → `/head-to-head…`, query and hash intact, `replace`. */
function HeadToHeadAlias() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/turn-based/, "");
  return (
    <Navigate
      to={{ pathname: `/head-to-head${rest}`, search: location.search, hash: location.hash }}
      replace
    />
  );
}

export default function App() {
  return (
    <ChunkErrorBoundary>
      <Router>
        {/* R2-5 — the app's ONE writer of `document.title`, mounted above
            `<Routes>` so it sees every navigation the router makes, including
            the `<Navigate>` aliases below (`/party` → `/doodleduel`) that never
            reach the server and so could never be titled by a static file.
            Outside `<Suspense>` on purpose: the tab is named while the page's
            chunk is still downloading, not after. */}
        <RouteTitle />
        {/* § F189 — an open tab never keeps running an old build. */}
        <StaleBuildReload />
        {/* § F181 — portrait on a phone, every route, by construction. */}
        <RotateGuard />
        {/* § F158 — THE WALL IS HERE, in front of every route below it. The hub,
            the four categories and every alias are inside it; the tab title
            (above) is not, so a gated tab is still named. */}
        <AppGate>
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            {/* 2026-07-27: `/` is the hub (Trivia + Doodle Duel), not the
                trivia app. § F158 (2026-09-06): the hub no longer "asks for
                nothing" — it is behind the app-wide gate like everything else,
                because a game selection listing the family's four surfaces was
                itself the thing Jake wanted a password in front of. */}
            <Route path="/" element={<HomePage />} />
            {/* § F118 — the one "Who Are You?" screen, reachable from the
                player chip on every surface (`?from=` returns you). */}
            <Route path="/who" element={<WhoPage />} />
            {/* § F201 — sign in / sign up by emailed code, groups, invites. */}
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/register" element={<AuthPage mode="register" />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/groups" element={<GroupsPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            {/* § F120 — your name and your face. */}
            <Route path="/me" element={<MePage />} />
            {/* § F170 — the two ROOM games are behind one door now (Jake:
                *"Create new menu item for all of the group game (Trivia, Doodle
                Duel). These games use the room structure."*). The GAMES did not
                move: `/trivia` and `/doodleduel` are the same routes they have
                always been, so every QR, texted link and bookmark still lands. */}
            <Route path="/group-games" element={<GroupGamesPage />} />
            {/* § F171 — challenge one person and play back and forth. Three
                screens, one component: the category menu, the duel list and one
                duel. The literal is declared BEFORE `/turn-based/wordle/:matchId`
                so it wins the match. */}
            <Route path="/head-to-head" element={<TurnBasedPage view="home" />} />
            <Route path="/head-to-head/wordle" element={<TurnBasedPage view="wordle" />} />
            <Route path="/head-to-head/wordle/:matchId" element={<TurnBasedPage view="match" />} />
            {/* § F198 — Head To Head Doodle: the same two screens, its own game. */}
            <Route path="/head-to-head/doodle" element={<TurnBasedPage view="wordle" game="doodle" />} />
            <Route path="/head-to-head/doodle/new/:opponentId" element={<TurnBasedPage view="doodle-new" game="doodle" />} />
            <Route path="/head-to-head/doodle/:matchId" element={<TurnBasedPage view="match" game="doodle" />} />
            <Route path="/trivia" element={<TrivianightPage />} />
            <Route path="/doodleduel" element={<PartyPage />} />
            {/* F45 (2026-08-06) — Jake: "this needs to be /gametable instead of
                a subdomain … better as part of goteamwilliams. Another category
                like trivia, doodle duel, and daily challenges." It holds no family
                data and its real access control is Bluetooth range, so it never
                had a door of its own; since § F158 it is behind the app-wide
                one like every other route. */}
            <Route path="/gametable" element={<GameTablePage />} />
            {/* § F220 — Puzzles → Pencil Pointers → one puzzle (its rooms). */}
            <Route path="/puzzles" element={<PuzzlesPage view="home" />} />
            <Route path="/puzzles/:kind" element={<PuzzlesPage view="kind" />} />
            <Route path="/puzzles/:kind/:puzzleId" element={<PuzzlesPage view="puzzle" />} />
            {/* F40 (2026-08-05) — the fourth hub category. FIVE routes, one
                component: the "Who Are You?" pick sits in front of all of them
                (§ F158 moved the family password out to `<AppGate>` above), and
                one chunk means the tiles are already loaded when a game page is
                opened. The literals are declared
                BEFORE `/daily-challenges/:gameId` so they win the match.
                `/daily-challenges/day/:date` is the calendar's destination (M2) — a
                previous day's tile grid; a game on that day is
                `/daily-challenges/:gameId?date=…`, so one route serves both days. */}
            <Route path="/daily-challenges" element={<DailyChallengesPage view="home" />} />
            <Route path="/daily-challenges/history" element={<DailyChallengesPage view="history" />} />
            <Route path="/daily-challenges/calendar" element={<DailyChallengesPage view="calendar" />} />
            <Route path="/daily-challenges/rankings" element={<DailyChallengesPage view="rankings" />} />
            <Route path="/daily-challenges/day/:date" element={<DailyChallengesPage view="day" />} />
            <Route path="/daily-challenges/:gameId" element={<DailyChallengesPage view="game" />} />
            {/* F26 — the old path, kept as a redirect for QRs and texted links. */}
            <Route path="/party" element={<PartyAlias />} />
            {/* § F169 — the pre-rename paths, kept for bookmarks and home
                screens. One wildcard covers the whole sub-tree. */}
            <Route path="/minigames" element={<DailyChallengesAlias />} />
            <Route path="/minigames/*" element={<DailyChallengesAlias />} />
            {/* Async challenges (design §8) were removed 2026-07-27 per Jake
                (requirements F2). `/party/c` and `/party/c/:slug` are no longer
                routes, so old links fall through to the hub like any other
                unknown path. */}
            {/* § F179 — the category was "Turn-Based Games" at `/turn-based` for
                its first day; those links (and one open match) still land.
                Down here with the other splat alias: a source guard that strips
                comments reads the splat as the start of one. */}
            <Route path="/turn-based" element={<HeadToHeadAlias />} />
            <Route path="/turn-based/*" element={<HeadToHeadAlias />} />
            <Route path="/trivia-night" element={<TriviaAlias />} />
            <Route path="*" element={<LegacyRedirect />} />
          </Routes>
        </Suspense>
        </AppGate>
      </Router>
    </ChunkErrorBoundary>
  );
}
